<script lang="ts" setup>
  import { computed } from 'vue';
  import { VTextField } from 'vuetify/components/VTextField';
  import { VTooltip } from 'vuetify/components/VTooltip';

  import BvCopyBtn from '@bv/components/form/copy-btn/BvCopyBtn.vue';
  import { getErrorMessages } from '@bv/helpers/form';
  import { toSystemColor } from '@bv/utils/common/color/to-system-color';

  import InfoIcon from '../partials/InfoIcon.vue';

  import type { ErrorObject } from '@vuelidate/core';

  type Value = any;

  defineOptions({
    inheritAttrs: false,
  });

  const modelValue = defineModel<Value>({ default: null });

  const props = withDefaults(
    defineProps<{
      required?: boolean;
      autofocus?: boolean;
      clearable?: boolean;
      size?: 'sm' | 'md';
      label?: string;
      appendIcon?: string | (string | [string, number])[];
      placeholder?: string;
      prefix?: string;
      suffix?: string;
      color?: string;
      bgColor?: string;
      disabled?: boolean;
      readonly?: boolean;
      error?: boolean;
      errorMessages?: string | string[] | ErrorObject | ErrorObject[];
      type?: 'text' | 'number' | 'password';
      showCopy?: boolean;
      copyText?: string;
      hint?: string;
      bottomHint?: string;
      disableWheelBehavior?: boolean;
    }>(),
    {
      required: false,
      autofocus: false,
      clearable: false,
      size: 'md',
      label: undefined,
      appendIcon: undefined,
      placeholder: undefined,
      prefix: undefined,
      suffix: undefined,
      color: 'base',
      bgColor: 'transparent',
      disabled: false,
      readonly: false,
      error: false,
      errorMessages: undefined,
      type: 'text',
      showCopy: false,
      copyText: undefined,
      hint: undefined,
      bottomHint: undefined,
      disableWheelBehavior: false,
    },
  );

  const colors = computed(() => ({
    color: toSystemColor(props.readonly ? 'gray-50' : props.color),
    bgColor: toSystemColor(props.bgColor),
    copyBtnBorderColor: props.disabled || props.readonly ? 'transparent' : undefined,
  }));

  const emit = defineEmits<{
    (event: 'keyup', payload: KeyboardEvent): void;
    (event: 'keydown', payload: KeyboardEvent): void;
    (event: 'keypress', payload: KeyboardEvent): void;
    (event: 'input', payload: Event): void;
    (event: 'change', payload: Event): void;
    (event: 'focus', payload: FocusEvent): void;
    (event: 'blur', payload: FocusEvent): void;
  }>();

  // get localized error message(s) from props.errorMessages
  const translatedMessage = computed(() => {
    return getErrorMessages(props.errorMessages);
  });

  // Handle number type conversion
  const internalValue = computed({
    get: () => modelValue.value,
    set: (value: any) => {
      if (props.type === 'number') {
        // Convert to number, or null if empty/invalid
        const numValue = value === '' || value === null || value === undefined ? null : Number(value);
        modelValue.value = numValue === null || isNaN(numValue) ? null : numValue;
      } else {
        modelValue.value = value;
      }
    },
  });
</script>

<template>
  <v-text-field
    v-model="internalValue"
    :append-icon="props.appendIcon"
    autocomplete="off"
    :autofocus
    class="bv-input [&_.v-field\_\_clearable]:hover:text-error-500 relative [&_.v-field\_\_clearable]:absolute [&_.v-field\_\_clearable]:end-0 [&_.v-field\_\_clearable]:top-1/2 [&_.v-field\_\_clearable]:-translate-y-1/2 [&_.v-field\_\_clearable]:bg-white/80 [&_.v-field\_\_clearable]:text-gray-500 [&_.v-text-field\_\_prefix]:min-h-auto! [&_.v-text-field\_\_prefix]:py-0!"
    :class="[
      {
        'bv-input--disabled': props.disabled,
        'bv-input--readonly': props.readonly,
        'bv-input--error': props.error || translatedMessage.length > 0,
        'bv-input--sm': props.size === 'sm',
        'bv-input--md': props.size === 'md',
      },
      $attrs.class,
    ]"
    :clearable
    :color="colors.color"
    :disabled
    :error="props.error"
    :error-messages="translatedMessage"
    hide-details="auto"
    hide-spin-buttons
    :label="props.label"
    persistent-placeholder
    :placeholder="props.placeholder || props.label"
    :prefix="props.prefix"
    :readonly
    rounded="lg"
    :suffix="props.suffix"
    :type="props.type"
    variant="outlined"
    @blur="emit('blur', $event)"
    @change="emit('change', $event)"
    @focus="emit('focus', $event)"
    @input="emit('input', $event)"
    @keydown="emit('keydown', $event)"
    @keypress="emit('keypress', $event)"
    @keyup="emit('keyup', $event)"
    @wheel="disableWheelBehavior ? $event.preventDefault() : () => {}"
  >
    <template
      v-if="$slots['append-inner'] || showCopy"
      #append-inner
    >
      <div class="bv-append-inner flex gap-1">
        <slot name="append-inner" />
        <bv-copy-btn
          v-if="showCopy"
          class="mt-0.25 px-1"
          :class="size === 'md' ? 'h-8' : 'h-6'"
          color="gray-700"
          :text="copyText"
          :value="internalValue"
          variant="outlined"
        />
      </div>
    </template>

    <template
      v-if="label"
      #label="{ label, props }"
    >
      <div
        v-bind="props"
        class="flex items-center gap-1"
      >
        <v-tooltip
          v-if="hint"
          location="top start"
          :text="hint"
          transition="fade-transition"
        >
          <template #activator="{ props: activatorProps }">
            <div
              v-bind="activatorProps"
              class="cursor-pointer"
            >
              <info-icon class="text-gray-600" />
            </div>
          </template>
        </v-tooltip>
        {{ label }}
        <span
          v-if="required"
          class="text-red-500"
        >
          <strong>*</strong>
        </span>
      </div>
    </template>
  </v-text-field>
  <p
    v-if="bottomHint"
    class="ms-3 mt-1 text-xs text-gray-500"
  >
    {{ bottomHint }}
  </p>
</template>

<style lang="scss" scoped>
  .bv-input {
    --bv-input-color: v-bind('colors.color');
    --bv-input-bg-color: v-bind('colors.bgColor');

    :deep(.v-field) {
      --v-disabled-opacity: 1;
      background-color: var(--bv-input-bg-color);

      &.v-field--focused .v-field__outline {
        --v-field-border-width: var(--bv-input-border-width);
      }

      .v-field__outline .v-field__outline__start {
        max-width: var(--bv-input-padding-inline-start);
      }

      &.v-field--appended {
        padding-inline-end: var(--bv-input-padding-inline-end);
      }

      .v-field__field,
      .v-field__field > *,
      .v-field__outline {
        min-height: var(--bv-input-height);
        height: var(--bv-input-height);
      }

      .v-field__field {
        &:is(.v-input:has(.v-input__append) *) {
          padding-inline-end: var(--bv-input-height);
        }

        input {
          font-size: 0.875rem;
          padding-inline-start: var(--bv-input-padding-inline-start);
          padding-inline-end: calc(var(--bv-input-gap) - var(--bv-input-icon-margin));

          &::placeholder {
            color: var(--bv-input-placeholder-color);
          }
        }
      }

      .v-text-field__prefix,
      .v-text-field__suffix {
        color: inherit;
      }
      .v-text-field__prefix {
        padding-inline-end: var(--bv-input-padding-inline-end);
      }

      .v-text-field__suffix {
        padding-inline-start: var(--bv-input-padding-inline-start);
      }

      .v-field-label--floating {
        pointer-events: auto;
        color: var(--bv-input-label-color) !important;
        background-color: var(--bv-input-label-bg-color);
        font-size: var(--bv-input-label-font-size) !important;
        line-height: var(--bv-input-label-line-height);
        padding: var(--bv-input-label-padding) !important;
        margin: 0;
        opacity: 1;
      }

      .v-field__outline {
        --v-field-border-opacity: 1;
        > * {
          color: var(--bv-input-border-color);
        }
      }

      &.v-field--error:not(.v-field--disabled) .v-field__prepend-inner > .v-icon,
      &.v-field--error:not(.v-field--disabled) .v-field__append-inner > .v-icon,
      &.v-field--error:not(.v-field--disabled) .v-field__clearable > .v-icon {
        color: var(--bv-input-error-color);
      }
    }

    :deep(.v-input__append) {
      position: absolute;
      top: 50%;
      margin-inline-start: 0;
      transform: translateY(-50%);
      inset-inline-end: 0.5rem;
      z-index: 1;
      color: var(--bv-input-color);
      height: var(--bv-input-icon-container-size);
      margin-inline-end: calc(var(--bv-input-icon-margin) * -1);
      justify-content: center;
      border-radius: var(--bv-input-icon-radius);
      background-color: var(--bv-input-icon-bg-color);
      aspect-ratio: 1/1;

      & > * {
        color: var(--bv-input-icon-color) !important;
        width: var(--bv-input-icon-size);
        height: var(--bv-input-icon-size);
        font-size: var(--bv-input-icon-size);
      }
    }
  }

  :deep(.bv-copy-btn) {
    margin-inline-start: var(--bv-input-gap);
    pointer-events: all;
    background-color: white;
    height: var(--bv-input-icon-container-size);
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-color: v-bind('colors.copyBtnBorderColor');
  }
</style>
